import React, { useState, useEffect } from 'react';
import { 
  FaBed, FaUsers, FaExpand, FaEye, FaWifi, FaCoffee, FaTv, FaShower, 
  FaCheck, FaTimes, FaInfoCircle, FaChevronDown, FaChevronUp,
  FaStar, FaCrown, FaLeaf, FaUmbrellaBeach, FaCity, FaMountain,
  FaSwimmingPool, FaParking, FaConciergeBell, FaDumbbell, FaChevronRight,
  FaCalendarAlt, FaCreditCard, FaShieldAlt, FaExclamationTriangle,
  FaTree, FaCloud, FaRecycle
} from 'react-icons/fa';
import hotelService from '../services/hotelService';
import {
  getAgentMarkupBreakdown,
  getDefaultCommissionPctForRateSource
} from '../utils/agentCommissionCart';
import { getOffMarketSpecialCommissionBreakdown, OFF_MARKET_DEFAULT_COMMISSION_PCT } from '../utils/offMarketSpecialCommission';

const ProfessionalConfirmationPopup = ({
  isOpen, 
  onClose, 
  pendingSelections = [], 
  priceCheckResponses = [], 
  searchCriteria = {}, 
  hotelData = {},
  onAddToCart,
  addToCartLoading = false,
  emissionsData = {},
  setEmissionsData,
  emissionsLoading = {},
  setEmissionsLoading,
  emissionsError = {},
  setEmissionsError,
  agentMarkupSettings = { loaded: false, markupEnabled: false, markupPercentage: 10 },
  /** Per-selection markup % overrides (same keys as selection.id) — must match RoomExplorerSelection. */
  roomMarkupOverrides = {},
  /** Off-market property data — when present, special commission changes the markup baseline. */
  offMarketProperty = null
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedRooms, setExpandedRooms] = useState({});
  // const [emissionsData, setEmissionsData] = useState({});
  // const [emissionsLoading, setEmissionsLoading] = useState({});
  // const [emissionsError, setEmissionsError] = useState({});
    const [allEmissionsLoaded, setAllEmissionsLoaded] = useState(false);


  // Reset emissions data when popup closes
  useEffect(() => {
    if (!isOpen) {
      setEmissionsData({});
      setEmissionsError({});
      setEmissionsLoading({});
      setAllEmissionsLoaded(false);
    }
  }, [isOpen]);

  // Calculate emissions for each item when popup opens
  useEffect(() => {
    if (isOpen && pendingSelections.length > 0 && priceCheckResponses.length > 0) {
      calculateAllEmissions();
    }
  }, [isOpen, pendingSelections, priceCheckResponses]);

  useEffect(() => {
    if (isOpen && pendingSelections.length > 0) {
      const allLoaded = pendingSelections.every((selection, index) => {
        const selectionId = selection?.id || index;
        return emissionsData[selectionId] !== undefined || 
               emissionsError[selectionId] !== undefined;
      });
      setAllEmissionsLoaded(allLoaded);
    }
  }, [emissionsData, emissionsError, pendingSelections, isOpen]);

  const isAnyEmissionsLoading = () => {
    return Object.values(emissionsLoading).some(loading => loading === true);
  };

  // Check if we can enable the Add to Cart button
  const canAddToCart = () => {
    // Don't enable if cart is already processing
    if (addToCartLoading) return false;
    
    // Don't enable if any emissions are still loading
    if (isAnyEmissionsLoading()) return false;
    
    // Enable if all emissions data is loaded (either successfully or with errors)
    return allEmissionsLoaded;
  };

  // useEffect(() => {
  //   if (Object.keys(emissionsData).length > 0 && onEmissionsDataUpdate) {
  //     onEmissionsDataUpdate(emissionsData);
  //   }
  // }, [emissionsData, onEmissionsDataUpdate]);

  const calculateAllEmissions = async () => {
    const newEmissionsData = {};
    const newEmissionsLoading = {};
    const newEmissionsError = {};

    // Initialize loading state for each item
    priceCheckResponses.forEach((response, index) => {
      const selectionId = pendingSelections[index]?.id || index;
      newEmissionsLoading[selectionId] = true;
    });

    setEmissionsLoading(newEmissionsLoading);

    // Calculate emissions for each item
    for (let i = 0; i < priceCheckResponses.length; i++) {
      const response = priceCheckResponses[i];
      const selection = pendingSelections[i];
      const selectionId = selection?.id || i;

      try {
        const hotelInfo = response?.HotelPriceCheckRS?.PriceCheckInfo?.HotelInfo;
        
        if (!hotelInfo) {
          newEmissionsError[selectionId] = 'Hotel information not available';
          continue;
        }

        const emissionsPayload = buildEmissionsPayload(hotelInfo, selection, i);
        
        const result = await hotelService.previewEmissions(emissionsPayload);
        
        if (result.success) {
          newEmissionsData[selectionId] = result.data;
        } else {
          throw new Error(result.message || 'Failed to calculate emissions');
        }
      } catch (error) {
        console.error(`Failed to calculate emissions for item ${i}:`, error);
        newEmissionsError[selectionId] = error.message || 'Unable to calculate sustainability impact';
      } finally {
        setEmissionsLoading(prev => ({
          ...prev,
          [selectionId]: false
        }));
      }
    }

    setEmissionsData(newEmissionsData);
    setEmissionsError(newEmissionsError);
  };

  const calculateEmissionsForItem = async (index) => {
    const response = priceCheckResponses[index];
    const selection = pendingSelections[index];
    const selectionId = selection?.id || index;

    setEmissionsLoading(prev => ({
      ...prev,
      [selectionId]: true
    }));
    setEmissionsError(prev => ({
      ...prev,
      [selectionId]: null
    }));

    try {
      const hotelInfo = response?.HotelPriceCheckRS?.PriceCheckInfo?.HotelInfo;
      
      if (!hotelInfo) {
        throw new Error('Hotel information not available');
      }

      const emissionsPayload = buildEmissionsPayload(hotelInfo, selection, index);
      const result = await hotelService.previewEmissions(emissionsPayload);
      
      if (result.success) {
        setEmissionsData(prev => ({
          ...prev,
          [selectionId]: result.data
        }));
      } else {
        throw new Error(result.message || 'Failed to calculate emissions');
      }
    } catch (error) {
      console.error(`Failed to calculate emissions for item ${index}:`, error);
      setEmissionsError(prev => ({
        ...prev,
        [selectionId]: error.message || 'Unable to calculate sustainability impact'
      }));
    } finally {
      setEmissionsLoading(prev => ({
        ...prev,
        [selectionId]: false
      }));
    }
  };

  const buildEmissionsPayload = (hotelInfo, selection, index) => {
    const address = hotelInfo?.LocationInfo?.Address;
    const countryName = address?.CountryName;
    const cityName = address?.CityName;
    
    return {
      hotelLocation: {
        country: countryName?.value || 'Singapore',
        countryCode: countryName?.Code || 'SG',
        state: cityName?.value || 'Singapore',
        city: cityName?.value || 'Singapore'
      },
      amenitiesList: getAmenitiesFromRooms(index),
      ecoCertified: false,
      occupancy: searchCriteria.adults || 1,
      roomType: getRoomTypeFromSelection(selection),
      selectedRoomType: getSelectedRoomType(index),
      numberOfNights: calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate),
      numberOfRooms: 1 // Since we're calculating per item
    };
  };

  const getAmenitiesFromRooms = (index) => {
    const amenities = new Set();
    
    // Extract numeric amenity codes from the specific room data
    const roomData = priceCheckResponses[index]?.HotelPriceCheckRS?.PriceCheckInfo?.HotelRateInfo?.Rooms?.Room?.[0];
    
    if (roomData?.Amenities?.Amenity) {
      const roomAmenities = Array.isArray(roomData.Amenities.Amenity) 
        ? roomData.Amenities.Amenity 
        : [roomData.Amenities.Amenity];
      
      roomAmenities.forEach(amenity => {
        if (amenity.Code) {
          amenities.add(amenity.Code);
        }
      });
    }
    
    // Add common amenities that might not be listed but are typically available
    const commonAmenities = [
      101, // WI_FI
      102, // TV
      103, // AIR_CONDITIONING
      104, // POOL
      105, // GYM
      106, // RESTAURANT
      107, // SPA
      108, // PARKING
    ];
    
    commonAmenities.forEach(code => {
      amenities.add(code);
    });
    
    // Check room description for additional amenities
    const roomDescription = roomData?.RoomDescription?.Text?.[0] || '';
    if (roomDescription.toLowerCase().includes('pool')) {
      amenities.add(104);
    }
    if (roomDescription.toLowerCase().includes('gym') || roomDescription.toLowerCase().includes('fitness')) {
      amenities.add(105);
    }
    if (roomDescription.toLowerCase().includes('parking')) {
      amenities.add(108);
    }
    if (roomDescription.toLowerCase().includes('restaurant') || roomDescription.toLowerCase().includes('dining')) {
      amenities.add(106);
    }
    if (roomDescription.toLowerCase().includes('spa')) {
      amenities.add(107);
    }
    if (roomDescription.toLowerCase().includes('bar') || roomDescription.toLowerCase().includes('wine')) {
      amenities.add(109);
    }
    
    return Array.from(amenities);
  };

  const getRoomTypeFromSelection = (selection) => {
    if (selection) {
      const roomType = selection.room?.roomTypeName || 
                      selection.room?.RoomType ||
                      'DELUXE';
      return roomType.toUpperCase().replace(/\s+/g, '_');
    }
    return 'STANDARD';
  };

  const getSelectedRoomType = (index) => {
    const roomData = priceCheckResponses[index]?.HotelPriceCheckRS?.PriceCheckInfo?.HotelRateInfo?.Rooms?.Room?.[0];
    
    if (roomData) {
      return {
        sabreRoomType: roomData.RoomType || 'DELUXE_ROOM',
        roomTypeCode: roomData.RoomTypeCode || 77
      };
    }
    return null;
  };

  // ... (keep the existing helper functions like calculateNights, formatCurrency, etc.)

  const calculateNights = (checkIn, checkOut) => {
    try {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      return Math.max(nights, 1);
    } catch (error) {
      console.error('Error calculating nights:', error);
      return 1;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatCarbonEmission = (kgCO2) => {
    if (!kgCO2) return '0 kg CO₂';
    if (kgCO2 >= 1000) {
      return `${(kgCO2 / 1000).toFixed(1)} t CO₂`;
    }
    return `${kgCO2.toFixed(1)} kg CO₂`;
  };

  const getTierColor = (tierName) => {
    if (!tierName) return 'text-gray-600 bg-gray-100 border-gray-200';
    
    switch (tierName.toLowerCase()) {
      case 'low impact':
        return 'text-green-600';
      case 'medium impact':
        return 'text-yellow-600';
      case 'high impact':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'low impact':
        return <FaLeaf className="text-green-500" />;
      case 'medium impact':
        return <FaCloud className="text-yellow-500" />;
      case 'high impact':
        return <FaTree className="text-red-500" />;
      default:
        return <FaRecycle className="text-gray-500" />;
    }
  };

  const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const deriveCancellationDeadline = (selection) => {
    const checkIn = toDate(selection?.checkInDate || searchCriteria?.checkInDate);
    const cancelPenaltyRaw = selection?.ratePlan?.ConvertedRateInfo?.CancelPenalties?.CancelPenalty;
    const cancelPenalty = Array.isArray(cancelPenaltyRaw) ? cancelPenaltyRaw[0] : cancelPenaltyRaw;
    const deadline = cancelPenalty?.Deadline;
    if (!deadline) return null;
    if (deadline.AbsoluteDeadline) {
      return toDate(deadline.AbsoluteDeadline);
    }
    // Fallback for offset-based policy shape from price-check response.
    if (
      checkIn &&
      typeof deadline.OffsetUnitMultiplier === 'number' &&
      typeof deadline.OffsetTimeUnit === 'string' &&
      deadline.OffsetTimeUnit.toLowerCase().includes('day')
    ) {
      const derived = new Date(checkIn);
      derived.setDate(derived.getDate() - Number(deadline.OffsetUnitMultiplier || 0));
      return derived;
    }
    return null;
  };

  const getSelectionPaymentMode = (selection) => {
    const daysBetween = (a, b) => (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
    const hoursBetween = (a, b) => (b.getTime() - a.getTime()) / (1000 * 60 * 60);

    const now = new Date();
    const checkInDate = toDate(selection?.checkInDate || searchCriteria?.checkInDate);
    const cancelPenaltyRaw = selection?.ratePlan?.ConvertedRateInfo?.CancelPenalties?.CancelPenalty;
    const cancelPenalty = Array.isArray(cancelPenaltyRaw) ? cancelPenaltyRaw[0] : cancelPenaltyRaw;
    const refundable = typeof cancelPenalty?.Refundable === 'boolean' ? cancelPenalty.Refundable : null;
    const cancellationDeadline = deriveCancellationDeadline(selection);

    if (refundable === false) return 'immediate';
    if (!checkInDate || !cancellationDeadline) return 'immediate';

    const x = daysBetween(now, checkInDate);
    const c = daysBetween(cancellationDeadline, checkInDate);
    const p = x - (c + 3);
    const hoursToDeadline = hoursBetween(now, cancellationDeadline);

    if (p <= 0) return 'immediate';
    if (hoursToDeadline < 72) return 'immediate';
    return 'scheduled';
  };

  const calculateTotalPrice = () => {
    return pendingSelections.reduce((total, selection) => total + selection.ratePlan.pricing.total, 0);
  };

  const calculateTotalBasePrice = () => {
    return pendingSelections.reduce((base, selection) => base + selection.ratePlan.pricing.base, 0);
  };

  /**
   * Off-market special commission breakdown for this hotel.
   * When present, the markup baseline shifts from the off-market default (6%) to the hotel's special total.
   */
  const isOffMarketStay =
    Boolean(offMarketProperty) ||
    searchCriteria?.offMarketStay === true ||
    searchCriteria?.offMarketStay === 1 ||
    searchCriteria?.offMarketStay === '1';

  const offMarketCommission =
    offMarketProperty?.specialCommission?.enabled
      ? getOffMarketSpecialCommissionBreakdown(offMarketProperty.specialCommission)
      : null;

  /** Commission floor per rate plan (7% Booking.com, 10% GDS, or off-market). */
  const getRateCommissionFloor = (ratePlan) => {
    if (offMarketCommission) return offMarketCommission.total;
    if (isOffMarketStay) return OFF_MARKET_DEFAULT_COMMISSION_PCT;
    return getDefaultCommissionPctForRateSource(ratePlan?.rateSource ?? ratePlan?.RateSource);
  };

  const getContextDefaultMarkup = (ratePlan) => {
    if (offMarketCommission) return offMarketCommission.total;
    if (isOffMarketStay) return OFF_MARKET_DEFAULT_COMMISSION_PCT;
    return getRateCommissionFloor(ratePlan);
  };

  /** Effective total commission % for a room line, floored at the rate-source minimum. */
  const getEffectiveMarkupPct = (selectionId, ratePlan) => {
    const floor = getRateCommissionFloor(ratePlan);
    const raw =
      roomMarkupOverrides && Object.prototype.hasOwnProperty.call(roomMarkupOverrides, selectionId)
        ? roomMarkupOverrides[selectionId]
        : getContextDefaultMarkup(ratePlan);
    return Math.max(floor, Number(raw) || floor);
  };

  /** Incremental % above the rate-source floor. */
  const getIncrementalPct = (pct, floor) =>
    Math.max(0, Math.round((pct - floor) * 10) / 10);

  /** Sum of incremental markup $ added on top of each pre-tax base. */
  const calculateTotalIncrementalMarkup = () => {
    if (!agentMarkupSettings.markupEnabled) return 0;
    return pendingSelections.reduce((sum, selection, index) => {
      const selectionId = selection.id ?? index;
      const floor = getRateCommissionFloor(selection.ratePlan);
      const pct = getEffectiveMarkupPct(selectionId, selection.ratePlan);
      const incPct = getIncrementalPct(pct, floor);
      const stayBase = selection.ratePlan.pricing?.base ?? 0;
      return sum + (stayBase * incPct) / 100;
    }, 0);
  };

  /** What the agent pays: pre-tax base totals + incremental markup only (taxes added at checkout). */
  const calculateTotalYouPay = () => calculateTotalBasePrice() + calculateTotalIncrementalMarkup();

  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  const handleAddAllToCart = async () => {
    if (onAddToCart) {
      await onAddToCart(pendingSelections, emissionsData);
    }
  };

  // Get hotel information for a specific item
  const getHotelInfo = (index) => {
    return priceCheckResponses[index]?.HotelPriceCheckRS?.PriceCheckInfo?.HotelInfo;
  };

  // Calculate total emissions across all items
  const calculateTotalEmissions = () => {
    return Object.values(emissionsData).reduce((total, data) => {
      return total + (data.carbonEmission || 0);
    }, 0);
  };

  // Return null if popup should not be shown
  if (!isOpen || !pendingSelections.length) {
    return null;
  }

  const nights = calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold">Booking Confirmation</h2>
            <p className="text-blue-100 mt-1">
              Review your {pendingSelections.length} selected room{pendingSelections.length > 1 ? 's' : ''}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'summary'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Summary ({pendingSelections.length})
          </button>
          <button
            onClick={() => setActiveTab('sustainability')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'sustainability'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Sustainability
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'details'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Room Details
          </button>
          {/* <button
            onClick={() => setActiveTab('policies')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'policies'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Policies
          </button> */}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="p-6 space-y-6">
              {/* Booking Overview */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-3">
                    <FaCalendarAlt className="text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Check-in</div>
                      <div className="text-gray-600">{new Date(searchCriteria.checkInDate).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FaCalendarAlt className="text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Check-out</div>
                      <div className="text-gray-600">{new Date(searchCriteria.checkOutDate).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FaUsers className="text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Duration</div>
                      <div className="text-gray-600">{nights} night{nights > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Room Summary Cards with Individual Sustainability */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Selected Rooms</h3>
                {pendingSelections.map((selection, index) => {
                  const selectionId = selection.id || index;
                  const itemEmissionsData = emissionsData[selectionId];
                  const itemEmissionsLoading = emissionsLoading[selectionId];
                  const itemEmissionsError = emissionsError[selectionId];
                  const hotelInfo = getHotelInfo(index);
                  const paymentMode = getSelectionPaymentMode(selection);
                  const paymentPillClass = paymentMode === 'immediate'
                    ? 'bg-rose-100 text-rose-800'
                    : 'bg-emerald-100 text-emerald-800';
                  const paymentLabel = paymentMode === 'immediate' ? 'Immediate' : 'Scheduled';

                  return (
                    <div key={selectionId} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <FaBed className="text-gray-600" />
                              <h4 className="font-semibold text-gray-900">{selection.room.roomTypeName}</h4>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center space-x-4">
                                <span>{selection.room.bedInfo}</span>
                                <span>•</span>
                                <span>{selection.room.roomView}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <FaUsers className="w-3 h-3" />
                                <span>Up to {selection.room.maxOccupancy.adults} adults, {selection.room.maxOccupancy.children} children</span>
                              </div>
                            </div>
                          </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {formatCurrency(selection.ratePlan.pricing.base)}
                                </div>
                                <div className="text-sm text-gray-600">for {nights} nights</div>
                                <div className="text-sm text-gray-500">
                                  {formatCurrency(selection.ratePlan.pricing.basePerNight)}/night
                                </div>
                              </div>
                        </div>

                        {/* Individual Sustainability Impact */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                            <FaLeaf className="mr-2 text-green-600" />
                            Environmental Impact
                          </h5>
                          {itemEmissionsLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              <span className="ml-2 text-sm text-gray-600">Calculating impact...</span>
                            </div>
                          ) : itemEmissionsError ? (
                            <div className="text-center py-2 text-gray-500">
                              <div className="flex items-center justify-center space-x-2">
                                <FaInfoCircle className="text-gray-400" />
                                <span className="text-sm">{itemEmissionsError}</span>
                              </div>
                              <button 
                                onClick={() => calculateEmissionsForItem(index)}
                                className="mt-1 text-blue-600 hover:text-blue-700 text-xs"
                              >
                                Try Again
                              </button>
                            </div>
                          ) : itemEmissionsData ? (
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="flex justify-center gap-3 text-center p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs text-gray-600">Carbon Emissions</div>
                                <div className="font-semibold text-gray-900">
                                  {formatCarbonEmission(itemEmissionsData.carbonEmission)}
                                </div>
                              </div>
                              <div className="flex text-center justify-center gap-3 p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs text-gray-600">Impact Level : </div>
                                <div className={`text-xs font-semibold ${getTierColor(itemEmissionsData.carbonTier?.tierName)}`}>
                                  {itemEmissionsData.carbonTier?.tierName || 'Unknown'}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-2 text-gray-500 text-sm">
                              Environmental impact data not available
                            </div>
                          )}
                        </div>

                        {/* Agent Commission Breakdown */}
                        {agentMarkupSettings.markupEnabled && (() => {
                          const floor = getRateCommissionFloor(selection.ratePlan);
                          const pct = getEffectiveMarkupPct(selectionId, selection.ratePlan);
                          const total = selection.ratePlan.pricing?.base ?? 0;
                          const br = getAgentMarkupBreakdown(total, pct, true, {
                            defaultCommissionPct: floor
                          });
                          const incPct = getIncrementalPct(pct, floor);
                          const incAmt = Math.round(total * (incPct / 100) * 100) / 100;
                          const youPay = Math.round((total + incAmt) * 100) / 100;
                          return (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className={`rounded-xl border overflow-hidden shadow-sm ${isOffMarketStay ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                                <div className={`flex items-center justify-between px-3 py-2 border-b ${isOffMarketStay ? 'border-amber-200' : 'border-slate-200'}`}>
                                  <h5 className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                                    <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    Your commission
                                  </h5>
                                  <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full border border-slate-300 bg-white text-slate-500">Advisors only</span>
                                </div>
                                {offMarketCommission && offMarketCommission.specialAdded > 0 && (
                                  <div className="px-3 py-2 border-b border-yellow-200/80 bg-gradient-to-r from-amber-50/95 via-yellow-50 to-amber-50/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">
                                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Special commission</p>
                                    <p className="mt-1 text-[10px] font-medium leading-snug text-slate-800 tabular-nums">
                                      <span className="text-slate-600">{offMarketCommission.defaultBase}%</span>
                                      <span className="mx-0.5 text-slate-400">+</span>
                                      <span className="font-semibold text-yellow-600">{offMarketCommission.specialAdded}%</span>
                                      <span className="mx-1 text-slate-300">=</span>
                                      <span className="font-bold text-slate-900">{offMarketCommission.total}% floor</span>
                                    </p>
                                  </div>
                                )}
                                <div className="divide-y divide-slate-200">
                                  <div className="flex justify-between items-center px-3 py-2 bg-white">
                                    <span className="text-xs text-slate-500">Base rate (stay)</span>
                                    <span className="text-xs font-semibold text-slate-700 tabular-nums">{formatCurrency(total)}</span>
                                  </div>
                                  <div className="flex justify-between items-start gap-2 px-3 py-2 bg-slate-50">
                                    <div>
                                      <span className="text-xs text-slate-500">Standard commission</span>
                                      <p className="text-[10px] text-slate-400 mt-0.5">Not added to total below</p>
                                    </div>
                                    <span className="text-xs font-semibold text-slate-500 tabular-nums shrink-0">{formatCurrency(br.defaultCommissionAmount)}</span>
                                  </div>
                                  <div className="flex justify-between items-center px-3 py-2 bg-white">
                                    <span className="text-xs font-semibold text-slate-700">Total commission at {pct}% (your earnings)</span>
                                    <span className="text-xs font-bold text-slate-900 tabular-nums">{formatCurrency(br.totalCommissionAmount)}</span>
                                  </div>
                                  {incPct > 0 && (
                                    <div className="flex justify-between items-center px-3 py-2 bg-slate-50">
                                      <span className="text-xs text-slate-500">
                                        Added to price ({incPct}% on base above {floor.toFixed(1)}%)
                                      </span>
                                      <span className="text-xs font-semibold text-slate-700 tabular-nums">+{formatCurrency(incAmt)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between items-center px-3 py-2 bg-slate-100">
                                    <span className="text-xs font-bold text-slate-800">Total you pay</span>
                                    <span className="text-sm font-bold text-slate-900 tabular-nums">{formatCurrency(youPay)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700">{selection.ratePlan.ratePlanName}</span>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              selection.ratePlan.refundable
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {selection.ratePlan.refundable ? (
                                <>
                                  <FaCheck className="w-3 h-3 mr-1" />
                                  Free Cancellation
                                </>
                              ) : (
                                <>
                                  <FaTimes className="w-3 h-3 mr-1" />
                                  Non-refundable
                                </>
                              )}
                            </div>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${paymentPillClass}`}>
                              <FaCreditCard className="w-3 h-3 mr-1" />
                              {paymentLabel}
                            </div>
                          </div>
                         {selection.ratePlan.refundable && (
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-700">
                                {selection.ratePlan.ConvertedRateInfo?.CancelPenalties?.CancelPenalty?.[0]?.Deadline ? (
                                  <>
                                    Cancellation Deadline : {
                                      selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetUnitMultiplier || ''
                                    } {
                                      selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetTimeUnit
                                        ? selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetTimeUnit.replace(/([A-Z])/g, ' $1').trim()
                                        : ''
                                    } {
                                      selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetDropTime
                                        ? selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetDropTime.replace(/([A-Z])/g, ' $1').trim()
                                        : ''
                                    } {
                                    selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.AbsoluteDeadline
                                      ? new Date(selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.AbsoluteDeadline)
                                          .toLocaleString('en-US', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true 
                                          })
                                      : ''
                                  }

                                  </>
                                ) : 'No cancellation deadline'}
                              </span>
                            </div>
                          )}
                          <button
                            onClick={() => toggleRoomExpansion(selectionId)}
                            className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                          >
                            {expandedRooms[selectionId] ? 'Show Less' : 'Show More'}
                            {expandedRooms[selectionId] ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
                          </button>
                        </div>

                        {/* Expanded Room Details */}
                        {expandedRooms[selectionId] && (
                          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {/* <div>
                                <h5 className="font-medium text-gray-900 mb-2">Cancellation Policy</h5>
                                <p className="text-gray-600">{selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetUnitMultiplier} {selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetTimeUnit.replace(/([A-Z])/g, ' $1').trim()} {selection.ratePlan.ConvertedRateInfo.CancelPenalties.CancelPenalty[0].Deadline.OffsetDropTime.replace(/([A-Z])/g, ' $1').trim()}</p>
                              </div> */}
                              <div>
                                <h5 className="font-medium text-gray-900 mb-2">Rate Details</h5>
                                <div className="space-y-1 text-gray-600">
                                  <div className="flex justify-between">
                                    <span>Base rate ({nights} nights):</span>
                                    <span>{formatCurrency(selection.ratePlan.pricing.base)}</span>
                                  </div>
                                  <div className="flex justify-between font-medium text-gray-900 pt-1 border-t border-gray-200">
                                    <span>Total:</span>
                                    <span>{formatCurrency(selection.ratePlan.pricing.base)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Overall Sustainability Impact Section */}
              {/* <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FaLeaf className="mr-2 text-green-600" />
                    Total Environmental Impact
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Combined carbon footprint of all selected rooms
                  </p>
                </div>
                
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  
                    <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FaCloud className="text-blue-600 text-xl" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatCarbonEmission(calculateTotalEmissions())}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Total Carbon Emissions</div>
                    </div>

                    <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FaLeaf className="text-green-600 text-xl" />
                      </div>
                      <div className="text-lg font-semibold text-gray-900 mb-2">
                        {pendingSelections.length} Room{pendingSelections.length > 1 ? 's' : ''}
                      </div>
                      <div className="text-sm text-gray-600">Combined Impact</div>
                    </div>

                    <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FaTree className="text-amber-600 text-xl" />
                      </div>
                      <div className="text-sm text-gray-600">Want to reduce impact?</div>
                      <button 
                        onClick={() => setActiveTab('sustainability')}
                        className="mt-2 text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        Learn More
                      </button>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Price Summary */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Subtotal ({pendingSelections.length} rooms):</span>
                    <span className="font-medium">
                      {formatCurrency(calculateTotalBasePrice())}
                    </span>
                  </div>
                  {agentMarkupSettings.markupEnabled && calculateTotalIncrementalMarkup() > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Additional markup (above rate-source floor on base)
                      </span>
                      <span className="font-semibold text-amber-800 tabular-nums">
                        +{formatCurrency(calculateTotalIncrementalMarkup())}
                      </span>
                    </div>
                  )}
                  <hr className="border-gray-300" />
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      {agentMarkupSettings.markupEnabled ? 'Total you pay:' : 'Total Amount:'}
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatCurrency(
                        agentMarkupSettings.markupEnabled ? calculateTotalYouPay() : calculateTotalBasePrice()
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    <span className="block mt-1">
                      Taxes, fees &amp; processing fees are added at checkout.
                    </span>
                    {agentMarkupSettings.markupEnabled && (
                      <span className="block mt-1">
                        Standard commission is not added again; only additional % above each rate&apos;s floor (7% Booking.com, 10% GDS) increases the amount you pay.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sustainability Tab - Updated to show all items */}
          {activeTab === 'sustainability' && (
            <div className="p-6 space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center">
                  <FaLeaf className="mr-2" />
                  Environmental Impact Breakdown
                </h3>
                <p className="text-green-800 text-sm">
                  Detailed carbon footprint analysis for each selected room
                </p>
              </div>

              {/* Individual Room Sustainability Cards */}
              <div className="space-y-4">
                {pendingSelections.map((selection, index) => {
                  const selectionId = selection.id || index;
                  const itemEmissionsData = emissionsData[selectionId];
                  const itemEmissionsLoading = emissionsLoading[selectionId];
                  const itemEmissionsError = emissionsError[selectionId];

                  return (
                    <div key={selectionId} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{selection.room.roomTypeName}</h3>
                      </div>

                      {itemEmissionsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                          <span className="ml-3 text-gray-600">Calculating impact...</span>
                        </div>
                      ) : itemEmissionsError ? (
                        <div className="text-center py-4 text-gray-500">
                          <FaInfoCircle className="mx-auto text-gray-400 mb-2" />
                          <p>{itemEmissionsError}</p>
                          <button 
                            onClick={() => calculateEmissionsForItem(index)}
                            className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Try Again
                          </button>
                        </div>
                      ) : itemEmissionsData ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Impact Metrics */}
                          <div className="space-y-4">
                            <div className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Carbon Footprint</h4>
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Emissions:</span>
                                  <span className="font-semibold text-lg">
                                    {formatCarbonEmission(itemEmissionsData.carbonEmission)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Impact Level:</span>
                                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTierColor(itemEmissionsData.carbonTier?.tierName)}`}>
                                    {itemEmissionsData.carbonTier?.tierName}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Energy Consumption</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Room Type:</span>
                                  <span>{itemEmissionsData.breakdown?.roomTypeConsumption?.toFixed(1)} kWh/day</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Amenities:</span>
                                  <span>{itemEmissionsData.breakdown?.amenitiesConsumption?.toFixed(1)} kWh/day</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Room Details */}
                          <div className="space-y-4">
                            <div className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Room Information</h4>
                              <div className="space-y-2 text-sm text-gray-600">
                                <div><strong>Type:</strong> {selection.room.roomTypeName}</div>
                                <div><strong>Bed:</strong> {selection.room.bedInfo}</div>
                                <div><strong>View:</strong> {selection.room.roomView}</div>
                                <div><strong>Occupancy:</strong> {selection.room.maxOccupancy.adults} adults, {selection.room.maxOccupancy.children} children</div>
                              </div>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Sustainability Tips</h4>
                              <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex items-start">
                                  <FaCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                  <span>Reuse towels to reduce water consumption</span>
                                </li>
                                <li className="flex items-start">
                                  <FaCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                  <span>Turn off lights and AC when leaving</span>
                                </li>
                                <li className="flex items-start">
                                  <FaCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                  <span>Use public transportation when possible</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <FaInfoCircle className="mx-auto text-gray-400 mb-2" />
                          <p>Environmental impact data not available for this room</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Total Impact Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Environmental Impact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {formatCarbonEmission(calculateTotalEmissions())}
                    </div>
                    <div className="text-sm text-gray-600">Total Carbon Emissions</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {pendingSelections.length}
                    </div>
                    <div className="text-sm text-gray-600">Rooms Selected</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {nights}
                    </div>
                    <div className="text-sm text-gray-600">Night{nights > 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>
            </div>
          )}


           {activeTab === 'details' && (
            <div className="p-6 space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
                <FaInfoCircle className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-yellow-800">Important Information</div>
                  <div className="text-yellow-700 mt-1">
                    Please review all room details, amenities, and policies before confirming your booking.
                  </div>
                </div>
              </div>

              {pendingSelections.map((selection, index) => (
                <div key={selection.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{selection.room.roomTypeName}</h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          <FaBed className="mr-2 text-gray-600" />
                          Room Configuration
                        </h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>Bed Type: {selection.room.bedInfo}</div>
                          <div>View: {selection.room.roomView}</div>
                          <div>Maximum Occupancy: {selection.room.maxOccupancy.adults} adults, {selection.room.maxOccupancy.children} children</div>
                        </div>
                      </div>

                      {/* <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          <FaWifi className="mr-2 text-gray-600" />
                          Amenities
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <FaWifi className="w-3 h-3" />
                            <span>Free WiFi</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaTv className="w-3 h-3" />
                            <span>Flat-screen TV</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaCoffee className="w-3 h-3" />
                            <span>Coffee maker</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaShower className="w-3 h-3" />
                            <span>Private bathroom</span>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* {activeTab === 'policies' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaShieldAlt className="mr-2 text-green-600" />
                      Cancellation Policy
                    </h3>
                    <div className="space-y-3 text-sm">
                      {pendingSelections.map((selection, index) => (
                        <div key={selection.id} className="pb-3 border-b border-gray-100 last:border-b-0">
                          <div className="font-medium text-gray-800">{selection.room.roomTypeName}</div>
                          <div className="text-gray-600 mt-1">{selection.ratePlan.cancellationDetails}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaCreditCard className="mr-2 text-blue-600" />
                      Payment Information
                    </h3>
                    <div className="text-sm text-gray-600 space-y-2">
                      <div>• No payment required at this time</div>
                      <div>• Payment will be collected at the hotel upon check-in</div>
                      <div>• Major credit cards accepted</div>
                      <div>• Valid ID required at check-in</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <FaExclamationTriangle className="mr-2 text-amber-600" />
                      Important Notes
                    </h3>
                    <div className="text-sm text-gray-600 space-y-2">
                      <div>• Check-in: 3:00 PM</div>
                      <div>• Check-out: 12:00 PM</div>
                      <div>• Late check-out may incur additional charges</div>
                      <div>• Hotel reserves the right to charge for incidentals</div>
                      <div>• Rates are subject to availability</div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Need Help?</h4>
                    <div className="text-sm text-blue-800">
                      Contact our customer service team if you have questions about your booking or need to make changes.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}
          {/* ... */}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 p-6 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Total:{' '}
            <span className="font-semibold text-gray-900 text-lg">
              {formatCurrency(
                agentMarkupSettings.markupEnabled ? calculateTotalYouPay() : calculateTotalBasePrice()
              )}
            </span>
            {agentMarkupSettings.markupEnabled && calculateTotalIncrementalMarkup() > 0 && (
              <span className="block text-[11px] text-teal-700/95 font-medium mt-0.5">
                Includes optional markup add-ons above each rate&apos;s commission floor
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddAllToCart}
              disabled={!canAddToCart()}
              className="px-6 py-2 bg-slate-800 text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[140px] justify-center"
            >
              {addToCartLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  {!canAddToCart() && (
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>
                    {!canAddToCart() ? 'Calculating Impact...' : 'Add to Cart'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalConfirmationPopup;